/**
 * Row-shaping helpers for the "Process bill" item manipulation actions
 * (Merge all in one / Merge and split / Reset).
 *
 * The arithmetic itself lives in the shared
 * dialogs/invoice-expense-item-table/split-utils, so this dialog and the
 * income/expense dialog split and merge identically. This module only adapts
 * that to the work-order bill's row shape.
 *
 * Keep it import-free beyond split-utils. Anything that reaches
 * src/util/util-formaters -- which includes Field, FieldsManager and
 * util-helpers -- crashes vitest at util-constants:1100. The dialog converts
 * its Field rows to and from the plain row shape used here.
 *
 * dialogs/charge-to is safe to import: it is import-free for the same reason.
 */

import {CHARGE_TO_FIELD} from '../../dialogs/charge-to';
import {
    buildSplitLabel,
    centsToMoney,
    computeSplitAmounts,
    mergeDescriptions,
    toCents
} from '../../dialogs/invoice-expense-item-table/split-utils';

/**
 * A money field holding a numeric 0 fails the 'empty' validator, whose check is
 * `!field.value` -- so an item could never be zeroed out by hand. The string
 * "0" is truthy and therefore passes, while getFieldKeyValues still hands the
 * API a numeric 0 through numberWithCommasToBack. Switching the field to
 * ['float'] instead is not an option: that routes TotalAmount into the
 * fillFieldsFromData numeric branch, which comma-formats thousands into
 * "1,234.56" and makes the grand total Number() to NaN.
 */
const ZERO_MONEY_VALUE = '0';

/** A money value in the form a money Field can hold, including a zero. */
export function moneyFieldValue(value) {
    return toCents(value) === 0 ? ZERO_MONEY_VALUE : value;
}

/** Total of every row TotalAmount, in cents, free of float drift. */
export function sumCents(rows = []) {
    return rows.reduce((sum, row) => sum + toCents(row?.TotalAmount), 0);
}

/**
 * What the charge-to dialog picked, as {field name, value} ready to put on a row.
 *
 * Read from the dialog's **field map**, not from its values: ModalSaveResource calls
 * onSubmit(getFieldKeyValues(fields), fields), and the first argument flattens a
 * select-search to its id alone. Taking the id from there left every row holding
 * {value: id, label: undefined}, which the table rendered as "Company" and which reopened
 * the dialog with an empty dropdown.
 *
 * Returns null for Company — nothing to set, the caller has already cleared the targets.
 */
export function chargeToSelection(chargeTo, dialogFields) {
    const name = CHARGE_TO_FIELD[Number(chargeTo)];

    if (!name) {
        return null;
    }

    const picked = dialogFields?.[name]?.value;

    return {
        name,
        value: picked?.value ? {value: picked.value, label: picked.label ?? ''} : ''
    };
}

/**
 * The first row that is charged to anyone wins, and brings its own account with it.
 * Returns null when every row is on the company.
 *
 * All three targets are carried, not just the contact: a work-order line can now be
 * charged to an organization or a truck, and merging must not quietly drop one.
 */
export function pickChargeTo(rows = []) {
    const charged = rows.find(row => !!row?.ChargeToContactID || !!row?.ChargeToOrganizationID || !!row?.ChargeToTruckID);

    if (!charged) {
        return null;
    }

    return {
        ChargeToContactID: charged.ChargeToContactID ?? '',
        ChargeToContact: charged.ChargeToContact ?? '',
        ChargeToOrganizationID: charged.ChargeToOrganizationID ?? '',
        ChargeToOrganization: charged.ChargeToOrganization ?? '',
        ChargeToTruckID: charged.ChargeToTruckID ?? '',
        ChargeToTruck: charged.ChargeToTruck ?? '',
        AccountID: charged.AccountID,
        Account: charged.Account
    };
}

/** Collapse every item and misc row into one row, preserving the grand total. */
export function buildMergedItem(items = [], misc = [], expenseAccount = {AccountID: '', Account: ''}) {
    const rows = [...items, ...misc];
    const chargeTo = pickChargeTo(rows);
    const account = chargeTo
        ? {AccountID: chargeTo.AccountID, Account: chargeTo.Account}
        : {AccountID: expenseAccount?.AccountID ?? '', Account: expenseAccount?.Account ?? ''};
    const total = moneyFieldValue(centsToMoney(sumCents(rows)));

    return {
        Description: mergeDescriptions(rows.map(row => row?.Description)),
        ChargeToContactID: chargeTo?.ChargeToContactID ?? '',
        ChargeToContact: chargeTo?.ChargeToContact ?? '',
        ChargeToOrganizationID: chargeTo?.ChargeToOrganizationID ?? '',
        ChargeToOrganization: chargeTo?.ChargeToOrganization ?? '',
        ChargeToTruckID: chargeTo?.ChargeToTruckID ?? '',
        ChargeToTruck: chargeTo?.ChargeToTruck ?? '',
        AccountID: account.AccountID,
        Account: account.Account,
        Qty: 1,
        Amount: total,
        TotalAmount: total
    };
}

/** Split one already-shaped row into `parts` rows that sum back to its total. */
export function splitRow(row, parts) {
    const count = Math.max(1, Math.floor(Number(parts)) || 1);
    const description = (row?.Description ?? '').toString().trim();

    return computeSplitAmounts(row?.TotalAmount, count).map((share, index) => {
        const amount = moneyFieldValue(share);

        return {
            ...row,
            Description: [description, `(${buildSplitLabel(index + 1, count)})`].filter(it => !!it).join(' '),
            Amount: amount,
            TotalAmount: amount
        };
    });
}

/**
 * Merge every row, then split the merged total into `parts` rows labelled
 * (1/n) ... (n/n). The parts sum back to the original grand total exactly.
 */
export function buildSplitItems(items = [], misc = [], parts, expenseAccount) {
    return splitRow(buildMergedItem(items, misc, expenseAccount), parts);
}
